import { Injectable } from "@angular/core";
import { Action } from "@ngrx/store";
import { ActionWithPayload, SYNC_SUFFIX } from "@obg/ngrx-utils";
import { AppError } from "@obg/ngx.core";

import { Credential, RegistrationResponse } from "../registration.model";

import {
	PersonalNumberVerificationSuccessResponse,
	RegistrationWithPersonalNumberCompletePayload,
	RegistrationWithPersonalNumberSimplePayload,
} from "./registration-with-personal-number.model";

export const ACTION_PREFIX = "[Registration With Personal Number]";

export const REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE = {
	/**
	 * Dispatched to start registration process.
	 */
	getRegistrationTokenSimpleFlow: `${ACTION_PREFIX} Get Registration Token Simple Flow`,
	getRegistrationTokenSimpleFlowSuccess: `${ACTION_PREFIX} Get Registration Token Simple Flow Success`,
	getRegistrationTokenSimpleFlowFail: `${ACTION_PREFIX} Get Registration Token Simple Flow Fail`,

	getRegistrationTokenCompleteFlow: `${ACTION_PREFIX} Get Registration Token Complete Flow`,
	getRegistrationTokenCompleteFlowSuccess: `${ACTION_PREFIX} Get Registration Token Complete Flow Success`,
	getRegistrationTokenCompleteFlowFail: `${ACTION_PREFIX} Get Registration Token Complete Flow Fail`,

	/**
	 * Verifies personal number. Currently only supports and required for SGA jurisdiction.
	 */
	verifyPersonalNumber: `${ACTION_PREFIX} Verify Personal Number`,
	verifyPersonalNumberSuccess: `${ACTION_PREFIX} Verify Personal Number Success`,
	verifyPersonalNumberFail: `${ACTION_PREFIX} Verify Personal Number Fail`,

	resetState: `${ACTION_PREFIX} Reset State ${SYNC_SUFFIX}`,
	resetRegistrationDetails: `${ACTION_PREFIX} Reset Registration Details ${SYNC_SUFFIX}`,
	resetPersonalNumber: `${ACTION_PREFIX} Reset Personal Number ${SYNC_SUFFIX}`,
	resetInformationLevel: `${ACTION_PREFIX} Reset Information Level ${SYNC_SUFFIX}`,
};

@Injectable({
	providedIn: "root",
})
export class RegistrationWithPersonalNumberAction {
	getRegistrationTokenSimpleFlow(payload: RegistrationWithPersonalNumberSimplePayload): ActionWithPayload {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenSimpleFlow,
			payload,
		};
	}

	getRegistrationTokenSimpleFlowSuccess(response: RegistrationResponse, credential?: Credential): ActionWithPayload {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenSimpleFlowSuccess,
			payload: {
				token: response.token,
				hashedEmail: response.hashedEmail,
				credential,
			},
		};
	}

	getRegistrationTokenSimpleFlowFail(error: AppError): ActionWithPayload {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenSimpleFlowFail,
			payload: error,
		};
	}

	getRegistrationTokenCompleteFlow(payload: RegistrationWithPersonalNumberCompletePayload): ActionWithPayload {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenCompleteFlow,
			payload,
		};
	}

	getRegistrationTokenCompleteFlowSuccess(
		response: RegistrationResponse,
		credential?: Credential
	): ActionWithPayload {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenCompleteFlowSuccess,
			payload: {
				token: response.token,
				hashedEmail: response.hashedEmail,
				credential,
			},
		};
	}

	getRegistrationTokenCompleteFlowFail(error: AppError): ActionWithPayload {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenCompleteFlowFail,
			payload: error,
		};
	}

	verifyPersonalNumber(personalNumber: string): ActionWithPayload {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.verifyPersonalNumber,
			payload: personalNumber,
		};
	}

	verifyPersonalNumberSuccess(payload: PersonalNumberVerificationSuccessResponse): ActionWithPayload {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.verifyPersonalNumberSuccess,
			payload,
		};
	}

	verifyPersonalNumberFail(error: AppError | Error): ActionWithPayload {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.verifyPersonalNumberFail,
			payload: error,
		};
	}

	resetState(): Action {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.resetState,
		};
	}

	resetPersonalNumber(): Action {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.resetPersonalNumber,
		};
	}

	resetInformationLevel(): Action {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.resetInformationLevel,
		};
	}

	resetRegistrationDetails(): Action {
		return {
			type: REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.resetRegistrationDetails,
		};
	}
}
